import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeonLoading } from '../components/LeonLoadingSystem';
import { 
  MessageCircle, 
  Send, 
  Loader2,
  User,
  Trash2,
  X,
  DollarSign,
  Info,
  AlertTriangle,
  Lightbulb,
  BookOpen,
  FileSearch,
  FolderPlus,
  Sparkles,
  Zap,
  Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import LeonAvatar from '../components/LeonAvatar';
import MobilePhotoUpload from '../components/MobilePhotoUpload';
import { LeonTutorial, TutorialWelcomeCard, useTutorial } from '../components/LeonTutorial';
import { LeadCaptureCTA } from '../components/LeadCapture';
import { BannerAd, useAdGate } from '../components/AdSystem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formats acceptés
const ACCEPTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Fonction pour parser les réponses structurées de Léon
function parseStructuredResponse(content) {
  const sections = {
    quickAnswer: null,
    explanation: null,
    techInfo: null,
    warning: null,
    leonAdvice: null,
    rawContent: content
  };

  // Patterns pour détecter les sections
  const patterns = {
    quickAnswer: /\[REPONSE_RAPIDE\]([\s\S]*?)(?=\[|$)/i,
    explanation: /\[EXPLICATION\]([\s\S]*?)(?=\[|$)/i,
    techInfo: /\[INFO_TECHNIQUE\]([\s\S]*?)(?=\[|$)/i,
    warning: /\[ATTENTION\]([\s\S]*?)(?=\[|$)/i,
    leonAdvice: /\[CONSEIL_LEON\]([\s\S]*?)(?=\[|$)/i
  };

  let hasStructure = false;

  Object.entries(patterns).forEach(([key, pattern]) => {
    const match = content.match(pattern);
    if (match && match[1]) {
      sections[key] = match[1].trim();
      hasStructure = true;
    }
  });

  // Si pas de structure détectée, traiter comme contenu brut
  if (!hasStructure) {
    sections.explanation = content;
  }

  return sections;
}

// Composant pour afficher une section de réponse
function ResponseSection({ type, content, icon: Icon, title, color }) {
  if (!content) return null;

  const colorClasses = {
    gold: 'bg-gold-500/10 border-gold-500/30 text-gold-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400'
  };

  const iconColorClasses = {
    gold: 'text-gold-500',
    blue: 'text-blue-500',
    orange: 'text-orange-500',
    emerald: 'text-emerald-500',
    purple: 'text-purple-500'
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg bg-zinc-900/50 flex items-center justify-center flex-shrink-0 ${iconColorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${iconColorClasses[color]}`}>
            {title}
          </p>
          <div className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant pour la réponse rapide (mise en avant)
function QuickAnswerCard({ content }) {
  if (!content) return null;

  // Extraire potentiellement un prix du contenu
  const priceMatch = content.match(/(\d+[\s,]*(?:à|-)?\s*\d*)\s*€/);
  const hasPrice = priceMatch !== null;

  return (
    <div className="bg-gradient-to-br from-gold-500/20 to-amber-500/10 border-2 border-gold-500/40 rounded-2xl p-5 mb-4">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gold-500/30 flex items-center justify-center flex-shrink-0">
          {hasPrice ? (
            <DollarSign className="w-6 h-6 text-gold-400" />
          ) : (
            <Zap className="w-6 h-6 text-gold-400" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-gold-400 text-xs font-bold uppercase tracking-wider mb-1">
            Réponse rapide
          </p>
          <p className="text-white text-lg font-medium leading-relaxed">
            {content}
          </p>
        </div>
      </div>
    </div>
  );
}

// Composant pour les boutons d'actions sous la réponse
function ActionButtons({ lastUserMessage }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [creatingRecipe, setCreatingRecipe] = useState(false);

  const handleCreateRecipe = async () => {
    if (!lastUserMessage || creatingRecipe) return;
    
    setCreatingRecipe(true);
    try {
      const response = await fetch(`${API_URL}/api/recipes/pending`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          project_description: lastUserMessage,
          from_chat: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/recipes/${data.recipe_id}`);
      } else {
        // Fallback to recipes page without context
        navigate('/recipes');
      }
    } catch (err) {
      // Fallback to recipes page without context
      navigate('/recipes');
    } finally {
      setCreatingRecipe(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-700/50">
      <button
        onClick={handleCreateRecipe}
        disabled={creatingRecipe}
        className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-violet-500/10 border border-violet-500/30 rounded-lg text-violet-400 hover:bg-violet-500/20 transition-colors text-[11px] sm:text-xs font-medium disabled:opacity-50"
      >
        {creatingRecipe ? (
          <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin" />
        ) : (
          <BookOpen className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        )}
        <span className="hidden sm:inline">Créer une recette travaux</span>
        <span className="sm:hidden">Recette</span>
      </button>
      <button
        onClick={() => navigate('/analyze')}
        className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors text-[11px] sm:text-xs font-medium"
      >
        <FileSearch className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        <span className="hidden sm:inline">Analyser un devis</span>
        <span className="sm:hidden">Devis</span>
      </button>
      <button
        onClick={() => navigate('/projects')}
        className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 hover:bg-emerald-500/20 transition-colors text-[11px] sm:text-xs font-medium"
      >
        <FolderPlus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        <span className="hidden sm:inline">Ajouter à un projet</span>
        <span className="sm:hidden">Projet</span>
      </button>
    </div>
  );
}

// Composant pour afficher un message de Léon de manière structurée
function LeonMessage({ content, showActions = true, lastUserMessage = '' }) {
  const sections = parseStructuredResponse(content);
  const navigate = useNavigate();

  // Si pas de structure, afficher le contenu de manière simple mais avec meilleur style
  if (!sections.quickAnswer && !sections.techInfo && !sections.warning && !sections.leonAdvice) {
    return (
      <div className="max-w-[700px]">
        <div className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap space-y-3">
          {content.split('\n\n').map((paragraph, i) => (
            <p key={i} className="leading-relaxed">{paragraph}</p>
          ))}
        </div>
        {showActions && <ActionButtons lastUserMessage={lastUserMessage} />}
      </div>
    );
  }

  return (
    <div className="max-w-[700px] space-y-4">
      {/* Réponse rapide - mise en avant */}
      <QuickAnswerCard content={sections.quickAnswer} />

      {/* Explication */}
      {sections.explanation && (
        <div className="text-zinc-300 text-sm leading-relaxed space-y-3">
          {sections.explanation.split('\n\n').map((paragraph, i) => (
            <p key={i} className="leading-relaxed">{paragraph}</p>
          ))}
        </div>
      )}

      {/* Info technique */}
      {sections.techInfo && (
        <ResponseSection
          type="tech"
          content={sections.techInfo}
          icon={Info}
          title="Information technique"
          color="blue"
        />
      )}

      {/* Attention / Avertissement */}
      {sections.warning && (
        <ResponseSection
          type="warning"
          content={sections.warning}
          icon={AlertTriangle}
          title="Point d'attention"
          color="orange"
        />
      )}

      {/* Conseil de Léon */}
      {sections.leonAdvice && (
        <ResponseSection
          type="advice"
          content={sections.leonAdvice}
          icon={Lightbulb}
          title="Conseil de Léon"
          color="emerald"
        />
      )}

      {/* Boutons d'actions */}
      {showActions && <ActionButtons lastUserMessage={lastUserMessage} />}
    </div>
  );
}

export default function AIChat() {
  const { user, isPremium, getAuthHeaders } = useAuth();
  const { startLoading, stopLoading } = useLeonLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingRequestId, setPendingRequestId] = useState(null);
  const messagesEndRef = useRef(null);

  // AdGate pour les utilisateurs gratuits (pub avant chaque question)
  const { requireAd, AdGateModal, isAdShowing } = useAdGate('chat');

  // Tutoriel Léon
  const {
    config: tutorialConfig,
    showWelcome,
    showTutorial,
    startTutorial,
    dismissWelcome,
    completeTutorial,
    closeTutorial,
    restartTutorial,
    disableAllTutorials
  } = useTutorial('chat');

  // Check if tutorial was requested via URL
  useEffect(() => {
    if (location.state?.startTutorial) {
      restartTutorial();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check for request_id from LeonHub (continuity flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestId = params.get('request_id');
    if (requestId) {
      setPendingRequestId(requestId);
      // Clean URL without reloading
      window.history.replaceState({}, document.title, '/chat');
    }
  }, [location.search]);

  useEffect(() => {
    fetchHistory();
  }, []);

  // Process pending request from LeonHub (continuity flow)
  useEffect(() => {
    const processPendingRequest = async () => {
      if (!pendingRequestId || loading || sending) return;
      
      try {
        // Fetch the original request from LeonHub
        const response = await fetch(`${API_URL}/api/leon/requests/${pendingRequestId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        if (response.ok) {
          const requestData = await response.json();
          const originalPrompt = requestData.prompt;
          const attachedImages = requestData.attached_images || [];
          
          // Check if there are attached images (for image_analysis intent)
          let imageData = null;
          if (attachedImages.length > 0) {
            imageData = attachedImages[0]; // Use first image
            // Set preview for display
            setImagePreview(imageData);
            setSelectedImage(imageData);
          }
          
          if (originalPrompt && originalPrompt.trim()) {
            // Set the input
            setInput(originalPrompt);
            setPendingRequestId(null);
            
            // Auto-send the question with image if present
            setTimeout(() => {
              handleSendWithMessage(originalPrompt, imageData);
            }, 100);
          } else if (imageData) {
            // Image only, no prompt - still send for analysis
            setPendingRequestId(null);
            setTimeout(() => {
              handleSendWithMessage('Analyse cette photo', imageData);
            }, 100);
          }
        } else {
          console.error('Failed to fetch pending request:', response.status);
        }
      } catch (error) {
        console.error('Error processing pending request:', error);
      } finally {
        setPendingRequestId(null);
      }
    };
    
    processPendingRequest();
  }, [pendingRequestId, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/history`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Callback pour MobilePhotoUpload - variante compacte
  const handleChatImageSelect = (file, preview) => {
    setImagePreview(preview);
    setSelectedImage(preview);
  };

  const handleChatImageRemove = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Send a specific message (used for continuity flow from LeonHub and suggestions)
  const handleSendWithMessage = (messageToSend, imageToSend = null) => {
    if ((!messageToSend?.trim() && !imageToSend) || sending || isAdShowing) return;
    
    // Utiliser requireAd qui affiche la pub pour les gratuits puis appelle le callback
    requireAd(async () => {
      const userMessage = messageToSend.trim();
      
      setInput('');
      setSelectedImage(null);
      setImagePreview(null);
      setSending(true);
      
      const loadingType = imageToSend ? 'analyse_photo' : 'chat';
      startLoading(loadingType);
      
      const displayContent = imageToSend 
        ? (userMessage ? `${userMessage}` : '[Photo envoyée pour analyse]')
        : userMessage;
      
      setMessages(prev => [...prev, {
        message_id: `temp_${Date.now()}`,
        role: 'user',
        content: displayContent,
        created_at: new Date().toISOString(),
        imagePreview: imageToSend
      }]);
      
      try {
        const response = await fetch(`${API_URL}/api/chat/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({ 
            message: userMessage || '',
            image: imageToSend || null
          })
        });
        
        if (response.status === 403) {
          const data = await response.json();
          setMessages(prev => [...prev, {
            message_id: `error_${Date.now()}`,
            role: 'assistant',
            content: data.detail,
            created_at: new Date().toISOString(),
            isError: true
          }]);
          return;
        }
        
        if (!response.ok) throw new Error('Erreur');
        
        const data = await response.json();
        setMessages(prev => [...prev, {
          message_id: data.message_id,
          role: 'assistant',
          content: data.message,
          created_at: new Date().toISOString()
        }]);
      } catch (error) {
        setMessages(prev => [...prev, {
          message_id: `error_${Date.now()}`,
          role: 'assistant',
          content: 'Une erreur est survenue. Veuillez réessayer.',
          created_at: new Date().toISOString(),
          isError: true
        }]);
      } finally {
        stopLoading();
        setSending(false);
      }
    });
  };

  const handleSend = () => {
    if ((!input.trim() && !selectedImage) || sending || isAdShowing) return;
    
    // Utiliser requireAd qui affiche la pub pour les gratuits puis appelle le callback
    requireAd(async () => {
      // Limiter à 5 questions maximum (10 messages: 5 user + 5 assistant)
      const MAX_QUESTIONS = 5;
      const userMessagesCount = messages.filter(m => m.role === 'user').length;
    
      if (userMessagesCount >= MAX_QUESTIONS) {
        // Garder seulement les 4 dernières questions (8 messages) pour faire place à la nouvelle
        const recentMessages = messages.slice(-8);
        setMessages(recentMessages);
      }
      
      const userMessage = input.trim();
      const imageToSend = selectedImage;
      
      setInput('');
      setSelectedImage(null);
      setImagePreview(null);
      setSending(true);
      
      // Utiliser le bon type de chargement selon le contexte
      const loadingType = imageToSend ? 'analyse_photo' : 'chat';
      startLoading(loadingType);
      
      // Add user message optimistically
      const displayContent = imageToSend 
        ? (userMessage ? `${userMessage}` : '[Photo envoyée pour analyse]')
        : userMessage;
      
      setMessages(prev => [...prev, {
        message_id: `temp_${Date.now()}`,
        role: 'user',
        content: displayContent,
        created_at: new Date().toISOString(),
        imagePreview: imageToSend // Store for display
      }]);
      
      try {
        const response = await fetch(`${API_URL}/api/chat/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({ 
            message: userMessage || '',
            image: imageToSend || null
          })
        });
        
        if (response.status === 403) {
          const data = await response.json();
          setMessages(prev => [...prev, {
            message_id: `error_${Date.now()}`,
            role: 'assistant',
            content: data.detail,
            created_at: new Date().toISOString(),
            isError: true
          }]);
          return;
        }
        
        if (!response.ok) throw new Error('Erreur');
        
        const data = await response.json();
        setMessages(prev => [...prev, {
          message_id: data.message_id,
          role: 'assistant',
          content: data.message,
          created_at: new Date().toISOString()
        }]);
      } catch (error) {
        setMessages(prev => [...prev, {
          message_id: `error_${Date.now()}`,
          role: 'assistant',
          content: 'Une erreur est survenue. Veuillez réessayer.',
          created_at: new Date().toISOString(),
          isError: true
        }]);
      } finally {
        stopLoading();
        setSending(false);
      }
    });
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir effacer l\'historique ?')) return;
    
    try {
      await fetch(`${API_URL}/api/chat/history`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      setMessages([]);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20 sm:pt-24 pb-4 sm:pb-6 px-3 sm:px-6">
      <div className="max-w-4xl mx-auto h-[calc(100vh-100px)] sm:h-[calc(100vh-120px)] flex flex-col">
        {/* Header - plus compact sur mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 sm:mb-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative">
              <LeonAvatar className="w-10 h-10 sm:w-14 sm:h-14" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-emerald-500 rounded-full border-2 border-black flex items-center justify-center">
                <Sparkles className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-heading font-bold text-white">
                Assistant by Léon
              </h1>
              <p className="text-zinc-500 text-xs sm:text-sm hidden sm:block">
                Votre expert travaux pédagogique
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button
              onClick={() => {
                if (window.confirm('Êtes-vous sûr de vouloir effacer la conversation ?')) {
                  setMessages([]);
                  // Also clear server-side history
                  fetch(`${API_URL}/api/chat/history`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: getAuthHeaders()
                  }).catch(err => console.error('Error clearing history:', err));
                }
              }}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-red-500 px-2 sm:px-3"
              data-testid="chat-clear-btn"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Effaceur</span>
            </Button>
          )}
        </motion.div>

        {/* Messages Area */}
        <div className="flex-1 glass-card overflow-hidden flex flex-col">
          <ScrollArea className="flex-1 p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="relative mb-6">
                  <LeonAvatar className="w-28 h-28" />
                  <motion.div 
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-gold-500 rounded-full flex items-center justify-center"
                  >
                    <MessageCircle className="w-4 h-4 text-black" />
                  </motion.div>
                </div>
                <h3 className="text-white font-heading font-bold text-2xl mb-3">
                  Bonjour ! Moi c'est Léon
                </h3>
                <p className="text-zinc-400 max-w-md mb-6 leading-relaxed">
                  Je suis ton assistant travaux. Je t'explique tout simplement, 
                  même les termes techniques. Pose-moi tes questions !
                </p>
                
                {/* Photo hint */}
                <div className="bg-gradient-to-r from-gold-500/10 to-amber-500/10 border border-gold-500/30 rounded-2xl p-5 mb-8 max-w-md">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-gold-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-gold-400 font-semibold text-sm">Analyse de photos</p>
                      <p className="text-zinc-500 text-xs mt-0.5">
                        Envoie une photo de ton problème et je t'aide à trouver une solution !
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Suggestions */}
                <p className="text-zinc-600 text-xs uppercase tracking-wider mb-3">Suggestions de questions</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {[
                    "Prix pose carrelage 60x60 ?",
                    "Comment reboucher un trou dans le placo ?",
                    "Normes électriques salle de bain ?"
                  ].map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => handleSendWithMessage(suggestion)}
                      disabled={sending || isAdShowing}
                      className="px-4 py-2.5 bg-zinc-800/80 rounded-full text-sm text-zinc-300 hover:text-gold-400 hover:bg-gold-500/10 hover:border-gold-500/30 border border-zinc-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((msg, i) => (
                  <motion.div
                    key={msg.message_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'user' ? (
                      // Message utilisateur
                      <div className="flex items-start gap-3 flex-row-reverse max-w-[80%]">
                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-zinc-700">
                          {user?.picture ? (
                            <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-zinc-400" />
                          )}
                        </div>
                        <div className="bg-gold-500/20 border border-gold-500/30 rounded-2xl rounded-tr-md p-4">
                          {/* Show image preview if user sent an image */}
                          {msg.imagePreview && (
                            <img 
                              src={msg.imagePreview} 
                              alt="Photo envoyée" 
                              className="max-w-[200px] rounded-lg mb-3"
                            />
                          )}
                          <p className="text-white text-sm leading-relaxed">{msg.content}</p>
                        </div>
                      </div>
                    ) : (
                      // Message de Léon
                      <div className="flex items-start gap-3 w-full">
                        <LeonAvatar className="w-9 h-9 flex-shrink-0" />
                        <div className={`flex-1 ${
                          msg.isError
                            ? 'bg-red-500/10 text-red-400 border border-red-500/20 rounded-2xl p-4 max-w-[80%]'
                            : ''
                        }`}>
                          {msg.isError ? (
                            <p className="text-sm">{msg.content}</p>
                          ) : (
                            <LeonMessage 
                              content={msg.content} 
                              showActions={i === messages.length - 1}
                              lastUserMessage={
                                // Find the last user message before this assistant message
                                messages.slice(0, i).filter(m => m.role === 'user').pop()?.content || ''
                              }
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
                {sending && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-start gap-3"
                  >
                    <LeonAvatar className="w-9 h-9" />
                    <div className="bg-zinc-800/50 rounded-2xl p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-400 text-sm">
                          Léon analyse ta question...
                        </span>
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Lead Capture CTA - après 3+ messages */}
          {messages.length >= 3 && (
            <div className="px-4 pb-2">
              <LeadCaptureCTA
                analysisType="chat"
                analysisId={null}
                projectSummary={messages.find(m => m.role === 'user')?.content?.substring(0, 100) || "Question travaux"}
              />
            </div>
          )}

          {/* Input Area - optimisé mobile */}
          <div className="p-3 sm:p-6 border-t border-white/5">
            <div className="flex gap-2 items-center">
              {/* MobilePhotoUpload en variante compacte */}
              <MobilePhotoUpload
                onImageSelect={handleChatImageSelect}
                onImageRemove={handleChatImageRemove}
                currentImage={imagePreview}
                variant="compact"
                accept="image/*,application/pdf"
                maxSize={10}
                disabled={sending}
              />
              
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={imagePreview ? "Décrivez votre photo..." : "Posez votre question..."}
                className="flex-1 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 h-[44px] text-base"
                disabled={sending}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={(!input.trim() && !selectedImage) || sending}
                className="bg-gold-500 hover:bg-gold-400 text-black min-w-[44px] h-[44px] px-3 sm:px-6"
                data-testid="chat-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            {/* Help text - caché sur très petit mobile */}
            <p className="text-zinc-600 text-xs mt-2 text-center hidden sm:block">
              Léon explique tout simplement, même les termes techniques.
              {!isPremium && <span className="text-zinc-700 ml-2">• Plan gratuit : messages limités</span>}
            </p>
          </div>
        </div>
      </div>
      
      {/* Tutorial Welcome Card */}
      <AnimatePresence>
        {showWelcome && messages.length === 0 && !loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <TutorialWelcomeCard 
              config={tutorialConfig}
              onStart={startTutorial} 
              onDismiss={dismissWelcome}
              onDisableTutorials={disableAllTutorials}
            />
          </div>
        )}
      </AnimatePresence>
      
      {/* Leon Tutorial Modal */}
      <LeonTutorial 
        isOpen={showTutorial}
        onClose={closeTutorial}
        onComplete={completeTutorial}
        config={tutorialConfig}
      />

      {/* Video Ad Modal (for free users before each question) */}
      <AdGateModal />

      {/* Sticky Banner Ad (bottom, for free users) */}
      {!isPremium && <BannerAd placement="bottom" sticky={true} />}
    </div>
  );
}
